import React from 'react'
import { useNavigate } from 'react-router-dom'
import { SIGNIN_LABELS, SIGNIN_STEP_KEYS } from '@constants'
import { useModal } from '@hooks/useModal'
import { useSiteContext } from '@contexts/MkSiteContext'
import { useAuthLabelsContext } from '@account/contexts/AuthLabelsContext'
import { SIGNIN_STEP_TO_BACKEND } from '@account/services/authLabels'
import { useSignInContext } from '@account/hooks/useSignInContext'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { EMkAuthenticationAction } from '@auth/types'
import { StepName } from '@account/components/Register/types'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'
import { ProtectedDataStore } from '@utils/ProtectedDataStore'
import { withBase } from '@utils/basePath'
import { formatErrorMessage } from '@utils/errorMessageFormatter'
import { clearRegistrationContext } from '@shared/utils/registrationContext'
import { FormFooter } from '../FormFooter'
import { FormHeader } from '../FormHeader'
import { FormError } from '../FormError'
import { getCurrentStep, getStepFooterLinks, getStepTitle, getStepCaption } from './stepRegistry'
import type { SignInViewState, FooterLinks, SignInStepName } from './types'

export interface SignInProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional title (e.g. from auth labels when opened as modal); falls back to step title when omitted */
  title?: string
  postSignIn?: () => void
  titleClassName?: string
  initialError?: string
  startStep?: SignInStepName
}

export const SignIn: React.FC<SignInProps> = ({ title: titleProp, postSignIn, titleClassName, initialError, startStep }) => {
  const navigate = useNavigate()
  const { themeBrand } = useSiteContext()
  const { getLabel } = useAuthLabelsContext()
  const { error: authError, isAccountLocked, lockoutEndTime, lockedUsername, dispatchAuthenticationAction } = useAuthentication()
  const { openSignInModal, openRegisterModal } = useModal({})
  const { setCurrentStep, state: signInState, updateField, resetContext } = useSignInContext()
  const [error, setError] = React.useState<Error | undefined>(undefined)
  const [message, setMessage] = React.useState<{ text: string; type: 'error' | 'info' } | undefined>(undefined)
  const [lockoutDismissed, setLockoutDismissed] = React.useState<boolean>(false)

  // Initialize view state with startStep if provided
  const initialStep = startStep || SIGNIN_STEP_KEYS.SIGNIN
  const initialView = startStep && startStep !== SIGNIN_STEP_KEYS.SIGNIN ? 'help-panel' : 'signin'

  const [viewState, setViewState] = React.useState<SignInViewState>({
    currentView: initialView,
    currentStep: initialStep,
  })

  // Set the current step in context when startStep is provided
  React.useEffect(() => {
    if (startStep && startStep !== SIGNIN_STEP_KEYS.SIGNIN) {
      setCurrentStep(startStep)
    }
  }, [startStep, setCurrentStep])

  // Sync local state with context state when context current step changes
  React.useEffect(() => {
    if (signInState.currentStep !== viewState.currentStep) {
      // Determine the view based on the step
      let newView: 'signin' | 'help-panel' = 'signin'
      if (signInState.currentStep === SIGNIN_STEP_KEYS.HELP_PANEL) {
        newView = 'help-panel'
      }

      setViewState({
        currentView: newView,
        currentStep: signInState.currentStep,
      })
    }
  }, [signInState.currentStep, viewState.currentStep])

  const step = React.useMemo(() => {
    const currentStep = getCurrentStep(viewState.currentStep)
    return currentStep
  }, [viewState.currentStep])

  const LazyStep = React.useMemo(() => {
    if (step?.loader) {
      return React.lazy(step.loader)
    }
    return React.lazy(() => Promise.resolve({ default: () => null }))
  }, [step])

  // Get step title and description from auth labels (API) with brand-aware registry fallback
  const backendStep = SIGNIN_STEP_TO_BACKEND[viewState.currentStep]
  const signInTitleFallback = React.useMemo(() => {
    const b = (themeBrand || '').toLowerCase()
    if ((b === 'ebac' || b === 'ev') && viewState.currentStep === SIGNIN_STEP_KEYS.SIGNIN) {
      return SIGNIN_LABELS.SIGN_IN_SUBMIT_BUTTON_TITLE
    }
    return getStepTitle(viewState.currentStep)
  }, [themeBrand, viewState.currentStep])
  const stepTitle = React.useMemo(
    () => (backendStep ? getLabel('signIn', backendStep, 'title', signInTitleFallback) : signInTitleFallback),
    [viewState.currentStep, backendStep, getLabel, signInTitleFallback],
  )
  const stepCaption = React.useMemo(
    () => (backendStep ? getLabel('signIn', backendStep, 'caption', getStepCaption(viewState.currentStep)) : getStepCaption(viewState.currentStep)),
    [viewState.currentStep, backendStep, getLabel],
  )
  const currentStep = React.useMemo(() => getCurrentStep(viewState.currentStep), [viewState.currentStep])

  // Handle step-specific icons
  const stepIcon = React.useMemo(() => {
    if (viewState.currentStep === SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_LINK) {
      return (
        <img
          src={withBase('/content/images/sir/check_your_email.gif')}
          alt={getLabel('signIn', 'passwordRecoveryEmailSent', 'altText', SIGNIN_LABELS.CHECK_EMAIL_ALT)}
          style={{ width: '160px', height: '160px' }}
        />
      )
    }
    return (currentStep?.props?.headerIcon ?? currentStep?.content?.icon) as React.ReactNode | undefined
  }, [currentStep, getLabel, viewState.currentStep])

  const stepIconClassName = (currentStep?.props?.headerIconClassName as string | undefined) ?? undefined
  const stepIconSet = (currentStep?.props?.headerIconSet as string | undefined) ?? undefined

  // Track if we've already handled the current auth error
  const [handledAuthError, setHandledAuthError] = React.useState<string | null>(null)

  // Reset handledAuthError when a new login attempt starts (loading transitions to true)
  React.useEffect(() => {
    if (signInState.loading) {
      setHandledAuthError(null)
    }
  }, [signInState.loading])

  // Handle initial error from props.
  // Keep displayed errors stable even if parent clears initialError after mount.
  React.useEffect(() => {
    if (!initialError) return

    setHandledAuthError(null)

    // Check if it's an inactivity message (info type)
    if (initialError.includes('inactivity')) {
      setError(undefined)
      setMessage({
        text: initialError,
        type: 'info',
      })
      return
    }

    // Format the error message to ensure it's user-friendly
    const formattedMessage = formatErrorMessage(initialError)
    const errorObj = {
      message: formattedMessage,
      stack: undefined,
      name: getLabel('signIn', 'loginForm', 'loginError', SIGNIN_LABELS.LOGIN_ERROR),
    }
    setMessage(undefined)
    setError(errorObj)
  }, [getLabel, initialError])

  // React.useEffect(() => {
  //   const handleKeyDown = (event: KeyboardEvent) => {
  //     if (event.key === 'Escape') {
  //       setError(undefined)
  //       stopErrorTimer() // Stop the timer when manually clearing
  //     }
  //   }
  //   window.addEventListener('keydown', handleKeyDown)
  //   return () => {
  //     window.removeEventListener('keydown', handleKeyDown)
  //   }
  // }, [stopErrorTimer])

  React.useEffect(() => {
    if (authError && authError.message !== handledAuthError) {
      // Check if this is an account lockout error - don't show inline, the lockout screen will handle it
      if (authError.message === 'OKTA_ACCOUNT_LOCKED' || isAccountLocked) {
        setHandledAuthError(authError.message)
        setError(undefined) // Clear any inline errors
        return
      }

      // Check if this is an MFA enrollment error
      if (authError.message === 'MFA_ENROLL_REQUIRED') {
        // Clear authentication error first to prevent infinite loop and ensure consistent state
        dispatchAuthenticationAction({ type: EMkAuthenticationAction.CLEAR_ERROR })
        setError(undefined)
        setHandledAuthError(authError.message)
        openRegisterModal(undefined, StepName.MFA_SETUP_SELECTOR)
        return
      }

      // Check if this is an MFA verification error
      if (authError.message === 'MFA_VERIFICATION_REQUIRED') {
        openRegisterModal(undefined, StepName.MFA_VERIFY_CODE)
        // Clear authentication error after modal is opened to prevent infinite loop
        dispatchAuthenticationAction({ type: EMkAuthenticationAction.CLEAR_ERROR })
        setError(undefined)
        setHandledAuthError(authError.message)
        return
      }

      // Check if this is a brand mismatch error
      if (authError.message === 'BRAND_MISMATCH') {
        const errorObj = {
          message: 'Your account is associated with a different brand than this application. Please contact support for assistance.',
          stack: undefined,
          name: SIGNIN_LABELS.LOGIN_ERROR,
        }
        setError(errorObj)
        setHandledAuthError(authError.message)
        return
      }

      // Show error inline for other login failures
      if (!initialError) {
        // Format the error message to ensure it's user-friendly
        const formattedMessage = formatErrorMessage(authError.message)
        const errorObj = {
          message: formattedMessage,
          stack: undefined,
          name: getLabel('signIn', 'loginForm', 'loginError', SIGNIN_LABELS.LOGIN_ERROR),
        }
        setError(errorObj)
        setHandledAuthError(authError.message)
      }
    }
  }, [authError, initialError, handledAuthError, isAccountLocked, openRegisterModal, dispatchAuthenticationAction])

  // Safety fallback: if we get OKTA_ACCOUNT_LOCKED but state hasn't flipped yet, force navigation to lock step
  // Only navigate if user is actively on the signin step (not when modal first opens)
  React.useEffect(() => {
    if (authError?.message === 'OKTA_ACCOUNT_LOCKED' && viewState.currentStep === SIGNIN_STEP_KEYS.SIGNIN) {
      setError(undefined)
      setHandledAuthError(null)

      // Reset form to initial state when showing account locked screen
      resetContext()

      setCurrentStep(SIGNIN_STEP_KEYS.ACCOUNT_LOCKED)
      setViewState({ currentView: 'signin', currentStep: SIGNIN_STEP_KEYS.ACCOUNT_LOCKED })
    }
  }, [authError?.message, setCurrentStep, viewState.currentStep, resetContext])

  // Handle password reset MFA required - transition to register modal
  React.useEffect(() => {
    const handleMfaRequired = () => {
      // Data already saved to registration context by SignIn context
      openRegisterModal(undefined, StepName.MFA_SETUP_SELECTOR)
    }

    window.addEventListener('passwordResetMfaRequired', handleMfaRequired as EventListener)
    return () => {
      window.removeEventListener('passwordResetMfaRequired', handleMfaRequired as EventListener)
    }
  }, [openRegisterModal])

  // Handle password reset MFA verification required - transition to verify code step
  React.useEffect(() => {
    const handleMfaVerificationRequired = () => {
      // Data already saved to registration context by SignIn context
      // For users with existing MFA enrollment, go directly to verify code step
      openRegisterModal(undefined, StepName.MFA_VERIFY_CODE)
    }

    window.addEventListener('passwordResetMfaVerificationRequired', handleMfaVerificationRequired as EventListener)
    return () => {
      window.removeEventListener('passwordResetMfaVerificationRequired', handleMfaVerificationRequired as EventListener)
    }
  }, [openRegisterModal])

  // Handle password reset success - following pattern from passwordResetMfaVerificationRequired (lines 182-194)
  React.useEffect(() => {
    const handlePasswordResetSuccess = () => {
      // Close current modal using existing modalClose event
      window.dispatchEvent(new CustomEvent('modalClose'))

      // Navigate to home
      navigate('/')

      // Small delay to ensure smooth modal transition
      setTimeout(() => {
        // Open sign-in modal with username pre-filled from localStorage
        openSignInModal(undefined, undefined, SIGNIN_STEP_KEYS.SIGNIN)
      }, 300)
    }

    window.addEventListener('passwordResetSuccess', handlePasswordResetSuccess as EventListener)
    return () => {
      window.removeEventListener('passwordResetSuccess', handlePasswordResetSuccess as EventListener)
    }
  }, [navigate, openSignInModal])

  // Check if account is currently locked and matches the active username
  const currentUsername = React.useMemo(() => String(signInState.formData?.username?.value || '').trim(), [signInState.formData?.username?.value])
  const isLockedUsernameMatch = React.useMemo(() => {
    if (!lockedUsername || !currentUsername) return false
    return lockedUsername.toLowerCase() === currentUsername.toLowerCase()
  }, [lockedUsername, currentUsername])

  const isCurrentlyLocked = React.useMemo(() => {
    if (!isAccountLocked) return false
    // For Okta lockouts, we might not have a lockoutEndTime, so just check isAccountLocked
    if (!lockoutEndTime) return isAccountLocked
    const locked = Date.now() < lockoutEndTime
    return locked
  }, [isAccountLocked, lockoutEndTime])

  // Don't auto-reset lockout dismissal when navigating to signin step
  // This allows the user to close the modal and reopen it without immediately seeing the lock screen
  // The lock screen will only show when the user actually tries to sign in (on form submission error)

  // Navigate to account-locked step when account is locked
  // Only navigate to lock screen if user is actively on the signin step (not when modal first opens)
  React.useEffect(() => {
    // Show lockout screen if account is currently locked AND user hasn't dismissed it
    // AND user is on the signin step (actively trying to sign in)
    // lockedUsername is set by the reducer when OKTA_ACCOUNT_LOCKED error occurs
    if (isCurrentlyLocked && isLockedUsernameMatch && !lockoutDismissed && viewState.currentStep === SIGNIN_STEP_KEYS.SIGNIN) {
      // Clear any existing errors before showing lockout screen
      setError(undefined)
      setHandledAuthError(null)

      // Reset form to initial state when showing account locked screen
      resetContext()

      setCurrentStep(SIGNIN_STEP_KEYS.ACCOUNT_LOCKED)
      setViewState({
        currentView: 'signin', // Keep in signin view so the modal stays open
        currentStep: SIGNIN_STEP_KEYS.ACCOUNT_LOCKED,
      })
    }
  }, [isCurrentlyLocked, isLockedUsernameMatch, lockoutDismissed, viewState.currentStep, setCurrentStep, resetContext])

  const handleHelpClick = () => {
    setViewState({
      currentView: 'help-panel',
      currentStep: SIGNIN_STEP_KEYS.HELP_PANEL,
    })
    // Also update the context's current step so executeRecoveryApi can find the correct API configuration
    setCurrentStep(SIGNIN_STEP_KEYS.HELP_PANEL)
  }

  const handleBackToSignInFromHelp = () => {
    // Clear any existing registration context to prevent username confusion from previous login attempts
    ProtectedDataStore.removeItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, { store: localStorage })

    setViewState({
      currentView: 'signin',
      currentStep: SIGNIN_STEP_KEYS.SIGNIN,
    })
    // Also reset the context's current step
    setCurrentStep(SIGNIN_STEP_KEYS.SIGNIN)
  }

  const handleRegisterClick = () => {
    openRegisterModal()
  }

  // Reset context when component unmounts if we're on any informational step
  React.useEffect(
    () => () => {
      // Define informational steps that should trigger reset on close
      const INFORMATIONAL_STEPS = [SIGNIN_STEP_KEYS.FORGOT_USERNAME_CONFIRMATION, SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_LINK, SIGNIN_STEP_KEYS.ACCOUNT_LOCKED]

      // Reset if we're on any informational step
      if (INFORMATIONAL_STEPS.includes(viewState.currentStep)) {
        resetContext()

        // If user is leaving the account locked screen, mark it as dismissed
        if (viewState.currentStep === SIGNIN_STEP_KEYS.ACCOUNT_LOCKED) {
          setLockoutDismissed(true)
        }
      }
    },
    [viewState.currentStep, resetContext],
  )

  // Handle close button click - reset context and clear error state for consistent experience
  React.useEffect(() => {
    const handleModalClose = () => {
      // Define informational steps that should trigger reset on close
      const INFORMATIONAL_STEPS = [SIGNIN_STEP_KEYS.FORGOT_USERNAME_CONFIRMATION, SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_LINK, SIGNIN_STEP_KEYS.ACCOUNT_LOCKED]

      // Always clear error and message state when modal is closed for consistent experience
      setError(undefined)
      setMessage(undefined)
      setHandledAuthError(null)

      // Clear authentication error from context so modal opens fresh next time
      if (authError) {
        dispatchAuthenticationAction({ type: EMkAuthenticationAction.CLEAR_ERROR })
      }

      // Reset if we're on any informational step
      if (INFORMATIONAL_STEPS.includes(viewState.currentStep)) {
        resetContext()

        // If user is closing the account locked screen, mark it as dismissed and clear registration context
        if (viewState.currentStep === SIGNIN_STEP_KEYS.ACCOUNT_LOCKED) {
          dispatchAuthenticationAction({ type: EMkAuthenticationAction.UNLOCK_ACCOUNT })
          setLockoutDismissed(true)
          // Clear registration context to ensure fresh state when modal reopens
          clearRegistrationContext()
          // Explicitly reset step to SIGNIN so modal reopens at sign-in step, not lock screen
          setCurrentStep(SIGNIN_STEP_KEYS.SIGNIN)
          setViewState({
            currentView: 'signin',
            currentStep: SIGNIN_STEP_KEYS.SIGNIN,
          })
        }
      }
    }

    // Listen for modal close events
    window.addEventListener('modalClose', handleModalClose)

    return () => {
      window.removeEventListener('modalClose', handleModalClose)
    }
  }, [viewState.currentStep, resetContext, authError, dispatchAuthenticationAction])

  // Memoize onClear callbacks to prevent infinite re-renders
  const handleErrorClear = React.useCallback(() => {
    setError(undefined)
    setHandledAuthError(null) // Allow showing error again on next attempt
  }, [])

  const handleMessageClear = React.useCallback(() => {
    setMessage(undefined)
  }, [])

  const showErrorBanner = !!error?.message
  const showMessageBanner = !showErrorBanner && !!message?.text

  // Get footer links from auth labels (API) with stepRegistry fallback for signin view
  const getFooterLinks = () => {
    if (viewState.currentView === 'signin') {
      const footerLinks = getStepFooterLinks(viewState.currentStep) as FooterLinks
      const needHelpText = getLabel('signIn', 'loginForm', 'needHelp', footerLinks.needHelp ?? '')
      const registerText = getLabel('signIn', 'loginForm', 'register', footerLinks.register ?? '')
      const links = []

      // Only add links if they have valid text
      if (needHelpText) {
        links.push({
          text: needHelpText,
          icon: 'chevron-right',
          iconPosition: 'right' as const,
          onClick: handleHelpClick,
          className: 'mk-sign-in-help-link',
        })
      }

      if (registerText) {
        links.push({
          text: registerText,
          icon: 'chevron-right',
          iconPosition: 'right' as const,
          onClick: handleRegisterClick,
          className: 'mk-sign-in-register-link',
        })
      }

      return links
    }
    return []
  }

  return (
    <div
      className="SignIn p-0 h-auto flex flex-col gap-6"
      data-testid="mk-sign-in-dialog">
      <FormHeader
        variant="hero"
        title={titleProp ?? stepTitle}
        titleClassName={titleClassName}
        caption={stepCaption}
        icon={stepIcon}
        iconSet={stepIconSet}
        iconClassName={stepIconClassName}
      />

      {showErrorBanner && error && error.message && (
        <FormError
          visible={showErrorBanner}
          message={error.message}
          type="error"
          autoClear={{
            enabled: true,
            duration: 25000,
            onClear: handleErrorClear,
          }}
        />
      )}

      {showMessageBanner && message && message.text && (
        <FormError
          visible={showMessageBanner}
          message={message.text}
          type={message.type ?? 'error'}
          autoClear={{
            enabled: true,
            duration: 180000, // 3 minutes
            onClear: handleMessageClear,
          }}
        />
      )}

      <React.Suspense fallback={<div className="w-full" />}>
        <LazyStep
          postSignIn={postSignIn}
          onUsernameChange={(username: string) => {
            updateField('username', username)
          }}
          onBackToSignIn={handleBackToSignInFromHelp}
          recoveredUsername={signInState.recoveredUsername}
          onSignInWithUsername={(username: string) => {
            DataStore.setItem('rememberMe', 'true', { store: localStorage })
            DataStore.setItem('savedUsername', username, { store: localStorage })
            updateField('rememberMe', true)
            updateField('username', username)
            window.location.reload()
          }}
          onPrevious={() => {
            setViewState({
              currentView: 'help-panel',
              currentStep: SIGNIN_STEP_KEYS.HELP_PANEL,
            })
            setCurrentStep(SIGNIN_STEP_KEYS.HELP_PANEL)
          }}
        />
      </React.Suspense>

      {viewState.currentView === 'signin' && viewState.currentStep !== 'account-locked' && <FormFooter links={getFooterLinks()} />}
    </div>
  )
}
